import type { SectionOption, UserPreferences } from "../types/domain";

export type SectionOptionListId = "hotelBookedWith" | "restaurantBookedWith" | "checklistCategories" | "participantRoles" | "transferModes";

const HOTEL_DEFAULTS: SectionOption[] = [
  { id: "booking", defaultKey: "hotels.providerBooking", archived: false, order: 0 },
  { id: "expedia", defaultKey: "hotels.providerExpedia", archived: false, order: 1 },
  { id: "agoda", defaultKey: "hotels.providerAgoda", archived: false, order: 2 },
  { id: "hotelSite", defaultKey: "hotels.hotelSite", archived: false, order: 3 },
  { id: "agency", defaultKey: "hotels.agency", archived: false, order: 4 },
  { id: "phone", defaultKey: "hotels.phone", archived: false, order: 5 },
  { id: "other", defaultKey: "common.other", archived: false, order: 6, allowCustomValue: true }
];

const RESTAURANT_DEFAULTS: SectionOption[] = [
  { id: "thefork", defaultKey: "restaurants.providerTheFork", archived: false, order: 0 },
  { id: "opentable", defaultKey: "restaurants.providerOpenTable", archived: false, order: 1 },
  { id: "google", defaultKey: "restaurants.providerGoogle", archived: false, order: 2 },
  { id: "restaurantSite", defaultKey: "restaurants.restaurantSite", archived: false, order: 3 },
  { id: "phone", defaultKey: "restaurants.phone", archived: false, order: 4 },
  { id: "other", defaultKey: "common.other", archived: false, order: 5, allowCustomValue: true }
];


const CHECKLIST_DEFAULTS: SectionOption[] = [
  { id: "action", defaultKey: "checklist.category.action", archived: false, order: 0 },
  { id: "documents", defaultKey: "checklist.category.documents", archived: false, order: 1 },
  { id: "booking", defaultKey: "checklist.category.booking", archived: false, order: 2 },
  { id: "health", defaultKey: "checklist.category.health", archived: false, order: 3 },
  { id: "other", defaultKey: "checklist.category.other", archived: false, order: 4 }
];


const TRANSFER_MODE_DEFAULTS: SectionOption[] = [
  { id: "car", defaultKey: "itinerary.transport.car", archived: false, order: 0 },
  { id: "bus", defaultKey: "itinerary.transport.bus", archived: false, order: 1 },
  { id: "plane", defaultKey: "itinerary.transport.plane", archived: false, order: 2 },
  { id: "train", defaultKey: "itinerary.transport.train", archived: false, order: 3 },
  { id: "ferry", defaultKey: "itinerary.transport.ferry", archived: false, order: 4 },
  { id: "other", defaultKey: "common.other", archived: false, order: 5, allowCustomValue: true }
];

const PARTICIPANT_ROLE_DEFAULTS: SectionOption[] = [
  { id: "owner", defaultKey: "participants.role.owner", archived: false, order: 0 },
  { id: "traveller", defaultKey: "participants.role.traveller", archived: false, order: 1 },
  { id: "participant", defaultKey: "participants.role.participant", archived: false, order: 2 },
  { id: "viewer", defaultKey: "participants.role.viewer", archived: false, order: 3 }
];

function cloneDefaults(list: SectionOption[]): SectionOption[] {
  return list.map((item) => ({ ...item }));
}

export function getDefaultSectionOptions(listId: SectionOptionListId): SectionOption[] {
  if (listId === "hotelBookedWith") return cloneDefaults(HOTEL_DEFAULTS);
  if (listId === "restaurantBookedWith") return cloneDefaults(RESTAURANT_DEFAULTS);
  if (listId === "checklistCategories") return cloneDefaults(CHECKLIST_DEFAULTS);
  if (listId === "participantRoles") return cloneDefaults(PARTICIPANT_ROLE_DEFAULTS);
  return cloneDefaults(TRANSFER_MODE_DEFAULTS);
}

export function normalizeSectionOptions(
  preferences: UserPreferences | null | undefined,
  listId: SectionOptionListId
): SectionOption[] {
  const source = preferences?.sectionOptions?.[listId];
  if (listId === "participantRoles") {
    const defaults = getDefaultSectionOptions(listId);
    if (!Array.isArray(source)) return defaults;
    return defaults.map((item) => {
      const saved = source.find((entry) => entry?.id === item.id);
      return saved?.label ? { ...item, label: saved.label } : item;
    });
  }
  if (!Array.isArray(source)) return getDefaultSectionOptions(listId);

  const seen = new Set<string>();
  const normalized = source.flatMap((raw, index) => {
    if (!raw || typeof raw !== "object") return [];
    const id = String(raw.id || "").trim();
    if (!id || seen.has(id)) return [];
    seen.add(id);
    return [{
      id,
      label: typeof raw.label === "string" ? raw.label.trim() : undefined,
      defaultKey: typeof raw.defaultKey === "string" ? raw.defaultKey : undefined,
      archived: Boolean(raw.archived),
      order: Number.isFinite(raw.order) ? Number(raw.order) : index,
      allowCustomValue: Boolean(raw.allowCustomValue)
    } satisfies SectionOption];
  });

  return normalized.sort((a, b) => a.order - b.order).map((item, index) => ({ ...item, order: index }));
}

export function optionDisplayLabel(option: SectionOption, t: (key: any) => string): string {
  if (option.label) return option.label;
  if (option.defaultKey) return t(option.defaultKey);
  return option.id;
}

export function migrateLegacyOptionValue(value: string, listId: SectionOptionListId, options: SectionOption[], t: (key: any) => string): string {
  if (!value) return "";
  if (options.some((item) => item.id === value)) return value;
  const normalized = value.trim().toLowerCase();
  const legacyMatches: Record<SectionOptionListId, Record<string, string>> = {
    hotelBookedWith: {
      "booking.com": "booking",
      expedia: "expedia",
      agoda: "agoda",
      hotelsite: "hotelSite",
      "sito dell’hotel": "hotelSite",
      "hotel website": "hotelSite",
      agency: "agency",
      agenzia: "agency",
      phone: "phone",
      telefono: "phone",
      other: "other",
      altro: "other"
    },
    restaurantBookedWith: {
      thefork: "thefork",
      opentable: "opentable",
      google: "google",
      restaurantsite: "restaurantSite",
      "sito del ristorante": "restaurantSite",
      "restaurant website": "restaurantSite",
      phone: "phone",
      telefono: "phone",
      other: "other",
      altro: "other"
    },
    checklistCategories: {
      action: "action",
      azione: "action",
      luggage: "action",
      bagaglio: "action",
      documents: "documents",
      documenti: "documents",
      booking: "booking",
      bookings: "booking",
      prenotazione: "booking",
      prenotazioni: "booking",
      health: "health",
      salute: "health",
      other: "other",
      altro: "other"
    },
    participantRoles: {
      owner: "owner",
      proprietario: "owner",
      traveller: "traveller",
      traveler: "traveller",
      viaggiatore: "traveller",
      participant: "participant",
      partecipante: "participant",
      guest: "participant",
      ospite: "participant",
      viewer: "viewer",
      visualizzatore: "viewer",
      child: "viewer",
      bambino: "viewer",
      other: "viewer",
      altro: "viewer"
    },
    transferModes: {
      auto: "car", car: "car", macchina: "car",
      autobus: "bus", bus: "bus",
      aereo: "plane", plane: "plane", flight: "plane",
      treno: "train", train: "train",
      traghetto: "ferry", ferry: "ferry",
      altro: "other", other: "other"
    }
  };
  const byLegacy = legacyMatches[listId][normalized];
  if (byLegacy && options.some((item) => item.id === byLegacy)) return byLegacy;
  const byLabel = options.find((item) => optionDisplayLabel(item, t).trim().toLowerCase() === normalized);
  return byLabel?.id || value;
}
